import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useRefState } from "../hooks/useRefState";
import { debounce } from 'throttle-debounce';
import { useEventContext } from "./EventContext";
import { useUserContext } from "./UserContext";
import { questionCreate, questionGet, questionsGet } from "../../../cp/apiPlayer";
import { Event } from "../../../cp/components/Comet";
import { usePlayerContext } from "./PlayerContext";
import { MODULE_QUESTIONS } from "../components/SideModule/Tabs";
import { fullscreenClose } from "../components/Player/FullScreen";
import { DEFAULT_MESSAGES_MODEL, DEFAULT_SCROLL_MODEL, FULLSCREEN_MODES } from "../constants/common";
import { FILTER_VALUES, FILTERS_EPISODE, MESSAGE_LIMIT, SORT_NEW_TO_OLD, SORT_POPULAR, SORT_QUERY, SORT_RATING, SORT_VALUES } from "../constants/message-types";
import { useNotificationsContext } from "./NotificationsContext";
import { useStreamContext } from "./StreamContext";
import storage from "../../../player/js/storage";
import { useWidgetContext } from "./WidgetContextProvider";
import { deleteMessages, flattenMessages, getMsgClientId, updateMessages } from "../helpers/messages-context";
import { uniqBy } from "../helpers/array";
import { checkTimeDifference } from "../helpers/times";

export const QuestionsContext = createContext(null);

export const useQuestionsContext = () => useContext(QuestionsContext);

const rolesSupportingAnswers = ["moderator", "organizer", "speaker", "technical_support"];

const getStorageData = (storageKeys) => Object.keys(storageKeys).reduce((acc, key) => {
    let value = storage.get(storageKeys[key]);

    if (value) {
        try {
            value = JSON.parse(storage.get(storageKeys[key]));
        } catch (e) {
            value = null;
        }
    }

    acc[key] = value || null;
    return acc;
}, {});

const getDefaultData = (streams, model, defaultData,) => streams.reduce((acc, stream) => {
    const { sort, filter } = defaultData || {};

    acc[stream?.questionRoom?.id] = model === 'message' ? {
        ...DEFAULT_MESSAGES_MODEL,
        sort: sort?.[stream?.questionRoom?.id] || SORT_POPULAR,
        filter: filter?.[stream?.questionRoom?.id] || FILTERS_EPISODE.all,
    } : DEFAULT_SCROLL_MODEL;
    return acc;
}, {});

export const QuestionsContextProvider = ({ children }) => {
    const { event } = useEventContext();
    const eventKey = event?.eventKey;
    const storageKeys = {
        filter: `event/${eventKey}/questions/filter/episode`,
        sort: `event/${eventKey}/questions/sort`
    };

    const { stream, streams = [] } = useStreamContext();
    const { id: streamId, streamKey, questionRoom } = stream || {};
    const questionRooms = (streams || []).map(({ questionRoom }) => questionRoom);
    const roomId = questionRoom?.id;
    const roomKey = questionRoom?.roomKey;

    const { widgetSettings } = useWidgetContext();
    const { user, isRoleUser, userRole } = useUserContext();
    const userId = user?.clientRegistrationId || user?.id;

    const { setFullScreen, setCurrentModule, tabletView, currentModule, currentEpisode: playerCurrentEpisode } = usePlayerContext();
    const { addMessage } = useNotificationsContext();

    const [data, setData] = useState(() => getDefaultData(streams, 'message', getStorageData(storageKeys)));
    const [scrollData, setScrollData] = useRefState(getDefaultData(streams, 'scroll'));
    const [scrollAtStart, setScrollAtStart] = useState(true);
    const [newMessages, setNewMessages] = useState([]);
    const [messageSent, setMessageSent] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [question, setQuestion] = useState('');
    const [isLoadingMore, setLoadingMore] = useState(false);

    const playerCurrentEpisodeRef = useRef(null);
    const streamsRef = useRef([]);

    const {
        enabled: moduleEnabled,
        allowAnonymousMessage: anonymousEnabled,
        isHide,
        allowReceiveQuestions,
        sortMethodType,
    } = event.moduleQuestion
    const enabledStreams = useMemo(() => streams
        .filter(str => !Array.isArray(widgetSettings?.allowQuestionsStreamIds) || widgetSettings.allowQuestionsStreamIds.includes(String(str.id)))
        .map(({ streamKey, questionRoom }) => ({ streamKey, roomId: questionRoom?.id, roomKey: questionRoom?.roomKey })), [streams, widgetSettings]);
    const enabled = moduleEnabled && enabledStreams.find((str) => str.streamKey === streamKey);
    const allowReceiveQuestionsPrevRef = useRef(allowReceiveQuestions);
    const isAnswerSupported = useMemo(() => user && isRoleUser(rolesSupportingAnswers), [user, userRole]);

    const { messages, sort, filter, hasMore } = data?.[roomId] || {};

    const sortValues = useMemo(() => {
        return sortMethodType ? SORT_VALUES.SORT_RATING : SORT_VALUES.SORT_POPULAR;
    }, []);

    const filterValues = useMemo(() => {
        return FILTER_VALUES;
    }, []);

    const filterVisible = (message) => (message.moderate || getMsgClientId(message) === userId);

    const sortMessages = (a, b) => {
        switch (sort) {
            case undefined:
            case SORT_NEW_TO_OLD:
                return b.time - a.time;
            case SORT_RATING:
                return (b.likes - b.dislikes) !== (a.likes - a.dislikes)
                    ? (b.likes - b.dislikes) - (a.likes - a.dislikes)
                    : b.time - a.time;
            case SORT_POPULAR:
                return (a.likes !== b.likes ? b.likes - a.likes : b.time - a.time);
            default:
                return;
        }
    };

    //Фильтруем сообщения и их потомков по признаку модерированности
    const visibleMessages = useMemo(() => (messages || [])
        .filter(filterVisible).map(message => ({ ...message, children: (message.children || []).filter(filterVisible) }))
        .sort(sortMessages), [messages, sort, roomId, userId]);

    //Разделяем закрепленные и обычные
    const regularMessages = visibleMessages.filter(({ fixed }) => !fixed);
    const fixedMessages = visibleMessages.filter(({ fixed }) => fixed).map((message) => {
        delete message.children;
        return message;
    });

    //Делаем одномерный массив и отделяем непрочитанные
    const flattenedMessages = useMemo(() => flattenMessages(regularMessages), [regularMessages, userId]);
    const unreadMessages = flattenedMessages.filter(({ unread, fixed, time }) => {
        return unread && !fixed && checkTimeDifference(time, 2);
    });
    const unreadParentMessages = unreadMessages.filter(({ parentId }) => !parentId);
    const unreadChildrenMessages = unreadMessages.filter(({ parentId }) => parentId);

    const isMessageUnread = useCallback((message) =>
        message && getMsgClientId(message) !== userId &&
        // message && getMsgClientId(message) !== userId && sort === SORT_NEW_TO_OLD &&
        (currentModule !== MODULE_QUESTIONS || !scrollData[message.roomId]?.scrollAtStart),
        [userId, currentModule, sort, scrollData]
    );

    const isChildMessageUnread = useCallback((parent, child) => parent && child && getMsgClientId(parent) === userId && getMsgClientId(child) !== userId, [userId])

    const handleMessages = (messages, newMessage, parentMessage) => {
        if (!Array.isArray(messages)) return [];
        const isUnread = newMessage.parentId ? isChildMessageUnread(parentMessage, newMessage) : isMessageUnread(newMessage);
        const index = messages.findIndex((message) => message.id === newMessage.id);
        if (index < 0) {
            return [...messages, { ...newMessage, unread: isUnread }]; //Все новые сообщения считаются непрочитанными, если проходят проверку
        } else {
            return messages.map((message) => (message.id === newMessage.id ?
                {
                    ...message,
                    //При обновлении существующего сообщения оно может стать непрочитанным только если прошло модерацию
                    unread: !message.moderate && newMessage.moderate && isUnread,
                    ...newMessage,
                    children: uniqBy([...(message.children || []), ...(newMessage.children || [])], 'id'),
                }
                : message));
        }
    };

    const updateCurrentData = (newData) => {
        setData(data => ({ ...data, [roomId]: ({ ...data?.[roomId], ...newData }) }))
    }

    const updateCurrentScrollData = (newData) => {
        setScrollAtStart(newData.scrollAtStart);
        setScrollData({ ...scrollData, [roomId]: ({ ...scrollData?.[roomId], ...newData }) })
    };

    const updateMessage = (message) => {
        setData(data => {
            if (!message) return data;

            const { roomId, parentId } = message;
            if (!data?.[roomId]) return data;

            let messages = data[roomId].messages || [];

            if (parentId) {
                const parent = messages.find(({ id }) => id === parentId);
                if (parent) {
                    parent.children = handleMessages(parent.children, message, parent);
                    messages = handleMessages(messages, parent);
                } else {
                    getParentMessageAndUpdate(message);
                }
            } else {
                messages = handleMessages(messages, message)
            }

            return { ...data, [roomId]: { ...data?.[roomId], messages } }

        })
    }

    const updateAllMessages = (updatedData) => setData(data => Object.keys(data).reduce((acc, roomId) => {
        acc[roomId] = { ...data?.[roomId], messages: updateMessages(data?.[roomId]?.messages, updatedData) }
        return acc;
    }, {}))

    const updateSort = (sort) => {
        storage.set(storageKeys.sort, JSON.stringify({ ...(getStorageData(storageKeys)['sort'] || {}), [roomId]: sort }), storage.MONTH)
        updateCurrentData({ sort });
        setLoading(true);
        getMessages(0, [{ roomId, roomKey, streamKey }], { sort });
    }

    const updateFilter = (filter) => {
        storage.set(storageKeys.filter, JSON.stringify({ ...(getStorageData(storageKeys)['filter'] || {}), [roomId]: filter }), storage.MONTH)
        updateCurrentData({ filter });
        setLoading(true);
        getMessages(0, [{ roomId, roomKey, streamKey }], { filter });
    }

    const getMessages = useCallback(async (offset = 0, streams = [], sortFilter) => {

        if (!moduleEnabled) return;

        await Promise.all(streams.map(async ({ roomId, roomKey }) => {
            try {
                const { sort, filter } = { ...(data?.[roomId] || {}), ...(sortFilter || {}) };
                const params = {
                    roomsKeys: [roomKey],
                    offset,
                    limit: MESSAGE_LIMIT,
                    withoutStreamKey: true,
                    sort: SORT_QUERY[sort],
                };

                if (filter === FILTERS_EPISODE.current && playerCurrentEpisode?.streamId === streamId) {
                    params.episodeKey = playerCurrentEpisode.key;
                }
                const questions = await questionsGet(eventKey, { ...params, moderate: true });
                let questionsModerating = (userId && !offset) ? await questionsGet(eventKey, { ...params, moderate: false, limit: 10 }) : [];
                questionsModerating = questionsModerating.filter(msg => getMsgClientId(msg) === userId);

                setData(data => ({
                    ...data,
                    [roomId]: {
                        ...data?.[roomId],
                        messages: uniqBy([...(offset ? (data?.[roomId]?.messages || []) : []), ...questions, ...questionsModerating], 'id'),
                        hasMore: questions.length === MESSAGE_LIMIT
                    }
                }));

            } catch (e) {
                console.error(e)
            }
        }))

        // if (!offset) {
        //     updateCurrentData({ lastScrollData: { scrollTop: 0, offset: 0 } });
        // }

        setLoading(false);
        setLoadingMore(false);

    }, [userId, data, playerCurrentEpisode, moduleEnabled, streamId]);

    const getMessagesMore = useCallback(() => {
        if (!messages) return;
        setLoadingMore(true);
        getMessages(messages.length, [{ roomId, roomKey, streamKey }]);
    }, [messages, roomId, roomKey, streamKey]);

    //Получаем родительское сообщение и добавляем к нему потомка, обновляем финальный массив
    const getParentMessageAndUpdate = async (message) => {
        if (!message) return;
        const _roomKey = questionRooms.find(({ id }) => message.roomId === id)?.roomKey;
        if (!_roomKey) return;
        const parentMessage = await questionGet(_roomKey, message.parentId);
        parentMessage.children = [...parentMessage.children, { ...message, unread: isChildMessageUnread(parentMessage, message) }];
        updateMessage(parentMessage);
    }

    useEffect(() => {
        if (!moduleEnabled) {
            setData(getDefaultData(streams, getStorageData(storageKeys)))
            return;
        };
        //Запрашиваем данные для всех активных залов
        if (enabledStreams.some(stream => !data[stream?.roomId]?.messages)) {
            setLoading(true);
            getMessages(0, enabledStreams.filter(stream => !data[stream?.roomId]?.messages));
        }
    }, [eventKey, moduleEnabled, enabledStreams]);

    useEffect(() => {
        if (newMessages.length) {
            if (!isLoading) {
                //Обрабатываем все новые сообщения
                newMessages.forEach(message => updateMessage(message));
            }
            setNewMessages([]);
        }
    }, [newMessages, isLoading])

    useEffect(() => {
        if (allowReceiveQuestionsPrevRef.current !== allowReceiveQuestions) {
            const message = {
                title: `questions.availableReceivingQuestions.${!!allowReceiveQuestions}`,
            };

            addMessage(message);
            allowReceiveQuestionsPrevRef.current = allowReceiveQuestions;
        }
    }, [allowReceiveQuestions]);

    useEffect(() => {

        if (filter === FILTERS_EPISODE.current && playerCurrentEpisode?.id && playerCurrentEpisode.id !== playerCurrentEpisodeRef.current?.id) {
            getMessages(0, [{ roomId, roomKey, streamKey }], { filter });
        }

        playerCurrentEpisodeRef.current = playerCurrentEpisode;

    }, [playerCurrentEpisode, filter]);

    const changeQuestion = useCallback(debounce(700, (value) => {
        setQuestion(value);
    }), []);

    const onWriteMessage = (data) => {
        const targetStream = streams.find(({ streamKey }) => streamKey === data?.streamKey);
        const _roomKey = targetStream?.questionRoom?.roomKey || roomKey;
        questionCreate(_roomKey, data).then(message => {
            if (message?.data && !message.data?.parentId) {
                setMessageSent(prev => !prev);
            }
        });
    }

    const onCreateFromCommentMessage = (message) => {
        setNewMessages(messages => [...messages, message]);

        if (user && getMsgClientId(message) === userId) {
            const _message = {
                title: 'questions.createFromComment',
                buttonTitle: 'messages.look',
                callback: () => {
                    setFullScreen(fullScreen => {
                        if (fullScreen && !tabletView) {
                            return FULLSCREEN_MODES.CHAT;
                        }
                        if (fullScreen && tabletView) {
                            fullscreenClose();
                        }
                        return null;
                    });
                    setCurrentModule(MODULE_QUESTIONS);
                },
                buttonRoute: '',
                pagesIsNotRoute: [`broadcast`, 'halls'],
            };
            addMessage(_message);
        }
    }

    const onDeleteMessage = (message) => message &&
        setData(data =>
        ({
            ...data,
            [message.roomId]: {
                ...(data[message.roomId] || {}),
                messages: deleteMessages((data[message.roomId]?.messages || []), parseInt(message.id))
            }
        }));

    const onReadAll = () => setData(data => data?.[roomId] ?
        ({ ...data, [roomId]: ({ ...data[roomId], messages: data[roomId].messages.map(m => ({ ...m, unread: false })) }) })
        : data
    );

    const onMoveToComments = (message) => message && setData((data) => ({
        ...data,
        [roomId]: {
            ...(data?.[roomId] || {}),
            messages: data?.[roomId]?.messages?.filter(({ id }) => id !== message.id),
        }
    }));

    const context = {
        enabled,
        anonymousEnabled,
        sort, filter,
        sortValues, filterValues,
        updateSort,
        updateFilter,
        updateMessage,
        flattenedMessages: isLoading ? [] : flattenedMessages,
        fixedMessages,
        unreadMessages,
        unreadParentMessages,
        unreadChildrenMessages,
        isLoading,
        question,
        hasMore,
        isLoadingMore,
        changeQuestion,
        getMessagesMore,
        isAvailableReceivingQuestions: enabled && allowReceiveQuestions,
        isShowBlockQuestions: enabled && !isHide,
        isAnswerSupported,
        messageSent,
        onWriteMessage,
        onReadAll,
        scrollData: scrollData?.[roomId] || {},
        scrollAtStart,
        updateScrollData: updateCurrentScrollData,
    };

    return (
        <QuestionsContext.Provider value={context}>
            <Event event={'create:question/message'} handler={(message) => setNewMessages(messages => [...messages, message])} />
            <Event event={'update:question/message'} handler={(message) => setNewMessages(messages => [...messages, message])} />
            <Event event={'delete:question/message'} handler={(message) => onDeleteMessage(message)} />
            <Event event={'create-from-comment:question/message'} handler={(message) => onCreateFromCommentMessage(message)} />
            <Event event={'create-from-question:comment/message'} handler={(message) => onMoveToComments(message)} />
            <Event event="moderate:question/message" handler={(moderate) => updateAllMessages({ moderate })} />
            {children}
        </QuestionsContext.Provider>
    );
}
